// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — **Record a Khipu** (the Khipu plan, solo recording, 2026-10-08):
 * started from Browse → Khipus, for every plan.
 *
 * While recording, a bar at the top of the page says so, with how many
 * steps there are, **Automatic | Manual**, **Add a note** (a line of text
 * for the step shown, its caption) and **Stop**. Automatic: each scene the
 * person rests on becomes a step (`soloRecorder`). Manual (owner,
 * 2026-10-09): a step only when **Record this view** is pressed. The mode
 * is remembered in this browser. **Your Khipu** closes with ✕ or Escape;
 * before it was saved or downloaded, ✕ asks to be pressed again. Stop opens **Your Khipu**: its name, **Play it**,
 * **Save to my account** (plans that keep Khipus), **Download (.zip)**
 * (the exchange copy on a plan that keeps none), **Edit it** (the Khipu
 * editor takes it over) and **Discard**. It is a draft story.
 *
 * Nothing leaves the page until the person saves or downloads it.
 * Written from scratch for AYNI: nothing here comes from TerraViz's code.
 */
import { getLocale, t } from '../i18n'
import { formatDate } from '../i18n/format'
import type { Khipu } from '../services/khipu'
import { newKhipuId } from '../services/khipu'
import { saveKhipu } from '../services/khipuStore'
import { zipKhipu } from '../services/lessonService'
import { buildSoloKhipu, createSoloRecorder, type SoloMode, type SoloRecorder, type SoloScene } from '../services/soloRecorder'
import { escapeAttr, escapeHtml } from './domUtils'

export interface SoloRecordHost {
  /** What the globe shows now. */
  scene(): SoloScene
  titleOf(datasetId: string): string | null
  layerTitleOf(layerId: string): string | null
  /** Whether the plan keeps Khipus on the account ("Save to my account"). */
  canKeep(): boolean
  /** Whether downloads are the minimal exchange copy (a plan that keeps no Khipus). */
  exchangeOnly(): boolean
  /** Play the recorded Khipu in the Khipu player. */
  play(k: Khipu): void
  /** Open the recorded Khipu in the Khipu editor (the recording is then the editor's). */
  edit?(k: Khipu): void
  announce(message: string): void
}

export interface SoloRecordHandle {
  isRecording(): boolean
  stop(): void
  /** Take everything away, recorded or not. */
  discard(): void
}

/** How often the globe is looked at, ms. */
const SAMPLE_MS = 1000
const MODE_KEY = 'ayni-khipu-record-mode'

function storedMode(): SoloMode {
  try {
    return localStorage.getItem(MODE_KEY) === 'manual' ? 'manual' : 'auto'
  } catch {
    return 'auto'
  }
}

function saveMode(mode: SoloMode): void {
  try {
    localStorage.setItem(MODE_KEY, mode)
  } catch {
    // storage refused: the mode lasts the visit
  }
}

let active: SoloRecordHandle | null = null

/** The recording under way or waiting to be saved, if any. */
export function activeSoloRecording(): SoloRecordHandle | null {
  return active
}

/** Start recording; a recording already under way is kept and shown. */
export function startSoloRecording(host: SoloRecordHost, opts: { returnFocus?: HTMLElement | null; now?: () => number } = {}): SoloRecordHandle {
  if (active) {
    document.querySelector<HTMLElement>('#khipu-rec-bar .khipu-rec-stop, #khipu-rec-done input')?.focus()
    return active
  }
  const now = opts.now ?? Date.now
  const recorder: SoloRecorder = createSoloRecorder(now, storedMode())
  const khipuId = newKhipuId()
  const started = new Date(now())
  let recording = true
  let noting = false
  let name = t('khipu.record.defaultName', { date: formatDate(started, { dateStyle: 'medium', timeStyle: 'short' }) })
  let status: { text: string; ok: boolean } | null = null
  let lastSteps = -1
  let wasFull = false
  /** Saved or downloaded since the last change: ✕ closes at once. */
  let kept = false
  let closeArmed = false

  const bar = document.createElement('section')
  bar.id = 'khipu-rec-bar'
  bar.className = 'khipu-rec-bar'
  bar.setAttribute('aria-label', t('khipu.record.aria'))
  document.body.appendChild(bar)

  const khipu = (): Khipu => {
    const lang = getLocale()
    return buildSoloKhipu(recorder.steps(), recorder.startedAt(), {
      id: khipuId,
      now: now(),
      title: { [lang]: name.trim() || t('khipu.record.untitled') },
      lang,
      titleOf: host.titleOf,
      layerTitleOf: host.layerTitleOf,
    })
  }

  function stepsText(): string {
    const n = recorder.steps().length
    return n === 1 ? t('khipu.record.steps.one') : t('khipu.record.steps.other', { n })
  }

  function render(): void {
    if (recording) {
      const manual = recorder.mode() === 'manual'
      bar.classList.remove('khipu-rec-done')
      bar.innerHTML = `
        <div class="khipu-rec-line">
          <span class="research-rec-dot khipu-rec-dot" aria-hidden="true"></span>
          <span class="khipu-rec-label">${escapeHtml(t('khipu.record.recording'))}</span>
          <span class="khipu-rec-count" role="status">${escapeHtml(stepsText())}${recorder.full() ? ` · ${escapeHtml(t('khipu.record.full'))}` : ''}</span>
          <span class="khipu-rec-mode" role="group" aria-label="${escapeAttr(t('khipu.record.mode'))}">
            <button type="button" class="khipu-rec-mode-btn" data-mode="auto" aria-pressed="${String(!manual)}" title="${escapeAttr(t('khipu.record.mode.autoHint'))}">${escapeHtml(t('khipu.record.mode.auto'))}</button>
            <button type="button" class="khipu-rec-mode-btn" data-mode="manual" aria-pressed="${String(manual)}" title="${escapeAttr(t('khipu.record.mode.manualHint'))}">${escapeHtml(t('khipu.record.mode.manual'))}</button>
          </span>
          ${manual ? `<button type="button" class="lesson-btn khipu-rec-capture"${recorder.full() ? ' disabled' : ''}>${escapeHtml(t('khipu.record.capture'))}</button>` : ''}
          <button type="button" class="lesson-btn lesson-btn-quiet khipu-rec-note-btn" aria-expanded="${String(noting)}">${escapeHtml(t('khipu.record.note'))}</button>
          <button type="button" class="lesson-btn khipu-rec-stop">${escapeHtml(t('khipu.record.stop'))}</button>
        </div>
        ${noting ? `<form class="khipu-rec-note">
          <label class="sr-only" for="khipu-rec-note-text">${escapeHtml(t('khipu.record.noteLabel'))}</label>
          <textarea id="khipu-rec-note-text" rows="2" maxlength="500" placeholder="${escapeAttr(t('khipu.record.notePlaceholder'))}"></textarea>
          <div class="khipu-rec-note-actions">
            <button type="button" class="lesson-btn lesson-btn-quiet khipu-rec-note-cancel">${escapeHtml(t('khipu.record.noteCancel'))}</button>
            <button type="submit" class="lesson-btn">${escapeHtml(t('khipu.record.noteAdd'))}</button>
          </div>
        </form>` : ''}`
      return
    }
    bar.classList.add('khipu-rec-done')
    const closeLabel = t(closeArmed ? 'khipu.record.closeConfirm' : 'khipu.record.close')
    bar.innerHTML = `
      <div class="khipu-rec-head">
        <h2 class="khipu-rec-title" id="khipu-rec-title">${escapeHtml(t('khipu.record.done'))}</h2>
        <button type="button" class="lesson-icon khipu-rec-close${closeArmed ? ' armed' : ''}" aria-label="${escapeAttr(closeLabel)}" title="${escapeAttr(closeLabel)}">&#x2715;</button>
      </div>
      ${closeArmed ? `<p class="lesson-save-status lesson-save-error" role="alert">${escapeHtml(t('khipu.record.closeConfirm'))}</p>` : ''}
      <p class="khipu-rec-summary">${escapeHtml(stepsText())}</p>
      <label class="khipu-rec-name-label" for="khipu-rec-name">${escapeHtml(t('khipu.record.name'))}</label>
      <input type="text" id="khipu-rec-name" class="khipu-rec-name" maxlength="120" value="${escapeAttr(name)}">
      <div class="khipu-rec-actions">
        <button type="button" class="lesson-btn khipu-rec-play"${recorder.steps().length ? '' : ' disabled'}>${escapeHtml(t('khipu.record.play'))}</button>
        ${host.edit ? `<button type="button" class="lesson-btn lesson-btn-quiet khipu-rec-edit">${escapeHtml(t('khipu.record.edit'))}</button>` : ''}
        ${host.canKeep() ? `<button type="button" class="lesson-btn lesson-btn-quiet khipu-rec-save">${escapeHtml(t('lessons.player.save'))}</button>` : ''}
        <button type="button" class="lesson-btn lesson-btn-quiet khipu-rec-download">${escapeHtml(t('khipu.player.download'))}</button>
        <button type="button" class="lesson-btn lesson-btn-quiet khipu-rec-discard">${escapeHtml(t('khipu.record.discard'))}</button>
      </div>
      <p class="lesson-notice">${escapeHtml(t(host.canKeep() ? 'khipu.record.hint' : 'khipu.record.hintDownload'))}</p>
      ${status ? `<p class="lesson-save-status${status.ok ? '' : ' lesson-save-error'}" role="status">${escapeHtml(status.text)}</p>` : ''}`
  }

  const timer = setInterval(() => {
    if (!recording) return
    recorder.observe(host.scene())
    const n = recorder.steps().length
    if (n !== lastSteps) {
      lastSteps = n
      // Redrawn only when the count changes, so a note being typed is never cleared.
      const count = bar.querySelector('.khipu-rec-count')
      if (count) count.textContent = `${stepsText()}${recorder.full() ? ` · ${t('khipu.record.full')}` : ''}`
      if (recorder.full() && !wasFull) {
        wasFull = true
        host.announce(t('khipu.record.full'))
      }
    }
  }, SAMPLE_MS)

  function stop(): void {
    if (!recording) return
    recording = false
    noting = false
    clearInterval(timer)
    render()
    bar.querySelector<HTMLElement>('.khipu-rec-name')?.focus()
    host.announce(t('khipu.record.stopped', { steps: stepsText() }))
  }

  function discard(): void {
    clearInterval(timer)
    bar.remove()
    if (active === handle) active = null
    const back = opts.returnFocus
    if (back?.isConnected) back.focus()
  }

  async function save(trigger: HTMLElement): Promise<void> {
    trigger.setAttribute('aria-busy', 'true')
    status = { text: t('lessons.player.saving'), ok: true }
    render()
    const result = await saveKhipu(khipu())
    if (result.ok) kept = true
    closeArmed = false
    status = result.ok
      ? { text: t('lessons.player.saved', { date: formatDate(new Date(result.value.keptUntil), { dateStyle: 'medium' }) }), ok: true }
      : { text: t(result.error === 'too_many' ? 'lessons.player.saveTooMany' : result.error === 'full' ? 'lessons.player.saveFull' : result.error === 'not_configured' ? 'lessons.player.saveUnavailable' : 'lessons.player.saveFailed'), ok: false }
    render()
    host.announce(status.text)
  }

  async function download(): Promise<void> {
    const { blob, filename } = await zipKhipu(khipu(), [], { exchange: host.exchangeOnly() })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 10_000)
    kept = true
    closeArmed = false
    status = { text: t('lessons.player.downloaded', { filename }), ok: true }
    render()
    host.announce(status.text)
  }

  /** ✕ or Escape on Your Khipu: closes at once once kept; otherwise asks for a second press. */
  function close(): void {
    if (kept || closeArmed || recorder.steps().length === 0) return discard()
    closeArmed = true
    render()
    bar.querySelector<HTMLElement>('.khipu-rec-close')?.focus()
    host.announce(t('khipu.record.closeConfirm'))
  }

  /** Manual mode: a step of the globe as it is now. */
  function capture(): void {
    if (!recorder.capture(host.scene())) return host.announce(t('khipu.record.full'))
    lastSteps = recorder.steps().length
    const count = bar.querySelector('.khipu-rec-count')
    if (count) count.textContent = `${stepsText()}${recorder.full() ? ` · ${t('khipu.record.full')}` : ''}`
    host.announce(t('khipu.record.captured', { n: recorder.steps().length }))
  }

  function addNote(): void {
    const box = bar.querySelector<HTMLTextAreaElement>('#khipu-rec-note-text')
    const text = box?.value ?? ''
    if (!text.trim()) {
      box?.focus()
      return
    }
    recorder.note(text)
    noting = false
    render()
    bar.querySelector<HTMLElement>('.khipu-rec-note-btn')?.focus()
    host.announce(t('khipu.record.noted'))
  }

  bar.addEventListener('click', (e) => {
    const button = (e.target as HTMLElement).closest<HTMLButtonElement>('button')
    if (!button || button.disabled) return
    if (button.classList.contains('khipu-rec-stop')) return stop()
    if (button.classList.contains('khipu-rec-capture')) return capture()
    if (button.classList.contains('khipu-rec-close')) return close()
    if (button.classList.contains('khipu-rec-mode-btn')) {
      const mode: SoloMode = button.dataset.mode === 'manual' ? 'manual' : 'auto'
      if (mode === recorder.mode()) return
      recorder.setMode(mode)
      saveMode(mode)
      render()
      bar.querySelector<HTMLElement>(mode === 'manual' ? '.khipu-rec-capture' : `.khipu-rec-mode-btn[data-mode="auto"]`)?.focus()
      host.announce(t(mode === 'manual' ? 'khipu.record.mode.manualOn' : 'khipu.record.mode.autoOn'))
      return
    }
    if (button.classList.contains('khipu-rec-note-btn')) {
      noting = !noting
      render()
      ;(bar.querySelector<HTMLElement>('#khipu-rec-note-text') ?? bar.querySelector<HTMLElement>('.khipu-rec-note-btn'))?.focus()
      return
    }
    if (button.classList.contains('khipu-rec-note-cancel')) {
      noting = false
      render()
      bar.querySelector<HTMLElement>('.khipu-rec-note-btn')?.focus()
      return
    }
    if (button.classList.contains('khipu-rec-play')) return host.play(khipu())
    if (button.classList.contains('khipu-rec-edit') && host.edit) {
      const k = khipu()
      discard()
      return host.edit(k)
    }
    if (button.classList.contains('khipu-rec-save')) return void save(button)
    if (button.classList.contains('khipu-rec-download')) return void download()
    if (button.classList.contains('khipu-rec-discard')) return discard()
  })
  bar.addEventListener('submit', (e) => {
    e.preventDefault()
    addNote()
  })
  bar.addEventListener('keydown', (e) => {
    const target = e.target as HTMLElement
    if (target.id === 'khipu-rec-note-text' && e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      addNote()
    } else if (target.id === 'khipu-rec-note-text' && e.key === 'Escape') {
      noting = false
      render()
      bar.querySelector<HTMLElement>('.khipu-rec-note-btn')?.focus()
    } else if (e.key === 'Escape' && !recording) {
      e.preventDefault()
      close()
    }
  })
  bar.addEventListener('input', (e) => {
    const target = e.target as HTMLInputElement
    if (target.id === 'khipu-rec-name') {
      name = target.value
      kept = false
    }
  })

  const handle: SoloRecordHandle = {
    isRecording: () => recording,
    stop,
    discard,
  }
  active = handle
  render()
  host.announce(t('khipu.record.started'))
  bar.querySelector<HTMLElement>('.khipu-rec-stop')?.focus()
  return handle
}
